import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query"
import { persistQueryClient } from "@tanstack/query-persist-client-core"
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister"
import * as Sentry from "@sentry/react-router"
import { syncIdentity } from "~/lib/identity.client"
import { creditProfileQueryKeys } from "~/store/creditProfile"
// Deep import, not the store barrel: `types` has no imports of its own, so the
// key prefix is available at this module's eval with no cycle risk.
import { ARV_PROMPT_TEMPLATE_KEY_PREFIX } from "~/store/admin/arv-ai-analysis/types"
import { termSheetKeys } from "~/store/admin/termsheets"
import { softCreditRepullQueryKeys } from "~/store/softCreditRepull"

// localStorage key the persisted React Query cache is written to. Kept in one
// place so the persister setup and cache-clearing helper stay in sync.
const PERSISTED_QUERY_CACHE_KEY = "backflip-query-cache"

/**
 * Query key PREFIXES that must NEVER be written to localStorage.
 *
 * `dehydrate()` consults only `shouldDehydrateQuery` — a short `gcTime` does
 * NOT prevent persistence, it only evicts after the write already landed.
 * Prefix (not whole-key) matching covers future parameterized siblings. The
 * credit profile carries names, addresses and credit-check dates — still
 * sensitive without the FICO number.
 *
 * The prompt templates are here for a different reason: a restored 24h-old
 * `active_id` is what the playground's drift + lost-update guards read, so a
 * persisted copy makes them silently answer for a version that is no longer
 * live (and the bodies are 20KB each).
 */
const NEVER_PERSIST_KEY_PREFIXES: ReadonlyArray<readonly unknown[]> = [
  creditProfileQueryKeys.prefix,
  ARV_PROMPT_TEMPLATE_KEY_PREFIX,
  // Term sheet inputs carry FICO and borrower names too.
  termSheetKeys.all,
  // Soft-pull history: FICO and borrower names.
  softCreditRepullQueryKeys.prefix,
]

/**
 * True when `queryKey` starts with every segment of `prefix`. Defensive over
 * `readonly unknown[]` — a shorter key simply does not match.
 */
const hasKeyPrefix = (
  queryKey: readonly unknown[],
  prefix: readonly unknown[]
): boolean =>
  prefix.length <= queryKey.length &&
  prefix.every((segment, index) => queryKey[index] === segment)

/**
 * Remove the persisted React Query cache from localStorage, so the impersonation
 * flows' full-page reload reboots with an empty cache instead of rehydrating the
 * previous identity's data. The in-memory queryClient is deliberately untouched:
 * clearing it makes still-mounted observers refetch under a half-swapped token.
 */
export const clearPersistedQueryCache = (): void => {
  if (typeof window === "undefined") return

  try {
    window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY)
  } catch (error) {}
}

// Type for user data from the "user" query
interface UserQueryData {
  me: {
    id: string
    firstName?: string
    lastName?: string
    email: string
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (increased for persistence)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
  // Global error handler - reports all errors to Sentry
  queryCache: new QueryCache({
    onError: (error) => {
      // Don't report 401 errors - they're expected and handled by auto-logout
      if ((error as { status?: number }).status !== 401) {
        Sentry.captureException(error)
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      // Don't report 401 errors - they're expected and handled by auto-logout
      if ((error as { status?: number }).status !== 401) {
        Sentry.captureException(error)
      }
    },
  }),
})

let resolveQueryCacheRestored!: () => void
/**
 * Settles once the persisted-cache restore below has finished (immediately
 * when there is no window, so nothing to restore). Restore hydrates the query
 * and mutation caches asynchronously after module load; a mutation fired
 * before it completes gets orphaned from its component's observer — the
 * request runs and succeeds in the cache, but the UI never sees it settle.
 * Anything that fires queries/mutations from a mount effect (e.g. the Cherre
 * inspector's ?address= auto-run) must await this first.
 */
export const queryCacheRestored = new Promise<void>((resolve) => {
  resolveQueryCacheRestored = resolve
})

// Initialize persistence immediately since SSR is disabled
if (typeof window !== "undefined") {
  const localStoragePersister = createAsyncStoragePersister({
    storage: window.localStorage,
    key: PERSISTED_QUERY_CACHE_KEY,
  })

  // Clear any existing session data from cache on app start
  queryClient.removeQueries({
    queryKey: ["plaid", "multiItemSession"],
  })
  queryClient.removeQueries({
    queryKey: ["plaid", "session"],
  })

  const [, restorePromise] = persistQueryClient({
    queryClient,
    persister: localStoragePersister,
    maxAge: 1000 * 60 * 60 * 24, // 24 hours
    dehydrateOptions: {
      // Never persist mutations: dehydration writes their `variables` (SSNs,
      // borrower PII) to localStorage, and nothing here ever calls
      // resumePausedMutations, so there is no upside to trade against.
      shouldDehydrateMutation: () => false,
      shouldDehydrateQuery: (query) => {
        const queryKey = query.queryKey
        // Don't persist sensitive data (see NEVER_PERSIST_KEY_PREFIXES)
        if (
          NEVER_PERSIST_KEY_PREFIXES.some((prefix) =>
            hasKeyPrefix(queryKey, prefix)
          )
        ) {
          return false
        }
        // Don't persist session-related queries as they're ephemeral
        if (Array.isArray(queryKey) && queryKey.length >= 2) {
          // Don't persist plaid session queries
          if (
            queryKey[0] === "plaid" &&
            (queryKey[1] === "multiItemSession" || queryKey[1] === "session")
          ) {
            return false
          }
        }
        return true
      },
    },
  })
  // Resolve on failure too — a broken restore must not wedge consumers.
  restorePromise.then(resolveQueryCacheRestored, resolveQueryCacheRestored)

  // Cache-level safety net for user identity in FullStory and Sentry.
  //
  // The PRIMARY path is the useUser-driven useIdentitySync() hook at the app
  // root; this subscriber additionally covers cache mutations a render might
  // not observe first (background invalidation, token swaps, clear()).
  //
  // Both route through syncIdentity(), which dedups by user id. We only SET from
  // "added"/"updated" (never clear, to avoid a transient undefined-data update
  // wrongly clearing identity mid-refetch) and clear only on "removed".
  queryClient.getQueryCache().subscribe((event) => {
    const queryKey = event.query.queryKey as string[]
    if (queryKey[0] !== "user") return

    if (event.type === "added" || event.type === "updated") {
      const user = (event.query.state.data as UserQueryData | undefined)?.me
      if (user) syncIdentity(user)
    } else if (event.type === "removed") {
      syncIdentity(null)
    }
  })
} else {
  resolveQueryCacheRestored()
}
